import { GATE_ORDER_PIPE, type GateKey } from '../employerVerificationCopy';
import {
  EMPLOYER_STREET_VIEW_TOUR_TARGET,
  checkpointTourTarget,
  getCheckpointTourCopy,
} from './checkpointTourCopy';
import type { ReviewReason, ReviewReasonSourceCheck } from './reviewReason.types';

export type EvidenceTourStepKind = 'checkpoint' | 'street-view';

export type EvidenceTourStep = {
  id: string;
  kind: EvidenceTourStepKind;
  target: string;
  title: string;
  description: string;
  /** Scannable review bullets shown in the guide panel. */
  lookFor?: string[];
  tip?: string;
  checkpointId?: string;
};

/** Commute / location red flags surface first in the guided path. */
const CHECKPOINT_PRIORITY: Record<string, number> = {
  COMMUTE_DISTANCE: 0,
  CROSS_PROVINCE_COMMUTE: 1,
  EMPLOYER_ADDRESS: 2,
  DIGITAL_FOOTPRINT: 3,
  AI_DIGITAL_FOOTPRINT: 4,
  WEB_DOMAIN: 5,
  DIRECT_PAYOR_MATCH: 6,
  AI_LINKAGE_ANALYSIS: 7,
  SECTOR_ROLE_MATCH: 8,
  AI_ROLE_SECTOR_ANALYSIS: 9,
};

function gateOrderIndex(gate: string): number {
  const idx = GATE_ORDER_PIPE.indexOf(gate as GateKey);
  return idx >= 0 ? idx : GATE_ORDER_PIPE.length;
}

function statusRank(status: string): number {
  const u = status.trim().toUpperCase();
  if (u === 'FAIL') return 3;
  if (u === 'REVIEW') return 2;
  if (u === 'PASS') return 1;
  return 0;
}

function checkpointPriority(step: string): number {
  const key = step.trim().toUpperCase();
  return CHECKPOINT_PRIORITY[key] ?? 50;
}

export function isSanityReviewOrFail(check: ReviewReasonSourceCheck): boolean {
  const status = check.status.trim().toUpperCase();
  return check.gate.trim().toUpperCase() === 'STRUCTURAL_SANITY' && (status === 'REVIEW' || status === 'FAIL');
}

export function isStreetViewTourStep(step: EvidenceTourStep): boolean {
  return step.kind === 'street-view';
}

function buildStreetViewGuideStep(checkpointId: string): EvidenceTourStep {
  return {
    id: `street-view-before-${checkpointId.replace(':', '-').toLowerCase()}`,
    kind: 'street-view',
    target: EMPLOYER_STREET_VIEW_TOUR_TARGET,
    title: 'Does this address look like a real workplace?',
    description:
      'The view turns slowly around the address the applicant gave us. Look at the building and ask: could someone actually work here for this employer?',
    lookFor: [
      'Can you see a door, sign, or business name from the street?',
      'Does the building fit the type of business (shop, office, warehouse, etc.)?',
      'Is the building the right size for the job they listed?',
    ],
    tip: 'If you are unsure, compare floors, parking or yard space, and overall building size with what you would normally expect for this employer and job title.',
    checkpointId: undefined,
  };
}

function sortSourceChecksForTour(
  checks: ReviewReasonSourceCheck[],
): ReviewReasonSourceCheck[] {
  const seen = new Set<string>();
  const unique = checks.filter((check) => {
    const key = `${check.gate}:${check.step}`.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return [...unique].sort((a, b) => {
    const priorityDiff = checkpointPriority(a.step) - checkpointPriority(b.step);
    if (priorityDiff !== 0) return priorityDiff;
    const hardA = a.isHardStop ? 1 : 0;
    const hardB = b.isHardStop ? 1 : 0;
    if (hardA !== hardB) return hardB - hardA;
    const statusDiff = statusRank(b.status) - statusRank(a.status);
    if (statusDiff !== 0) return statusDiff;
    const gateDiff = gateOrderIndex(a.gate) - gateOrderIndex(b.gate);
    if (gateDiff !== 0) return gateDiff;
    return a.step.localeCompare(b.step);
  });
}

/** Checkpoint guided path — street view guide before each structural sanity REVIEW/FAIL check. */
export function buildFindingEvidenceTourSteps(finding: ReviewReason): EvidenceTourStep[] {
  const sortedChecks = sortSourceChecksForTour(finding.sourceChecks);
  const steps: EvidenceTourStep[] = [];

  for (const check of sortedChecks) {
    const checkpointId = `${check.gate}:${check.step}`;
    if (isSanityReviewOrFail(check)) {
      steps.push(buildStreetViewGuideStep(checkpointId));
    }

    const copy = getCheckpointTourCopy(check.step, finding);
    steps.push({
      id: `checkpoint-${check.gate}-${check.step}`.toLowerCase(),
      kind: 'checkpoint',
      target: checkpointTourTarget(check.gate, check.step),
      title: copy.title,
      description: copy.description,
      lookFor: copy.lookFor,
      checkpointId,
    });
  }

  return steps;
}
